import * as React from 'react';
import {Table} from 'antd';
import {ErrorAlert} from '../common/ErrorAlert';
import {ColumnFilterItem, ColumnProps} from 'antd/lib/table';
import {ErrorResponse, GetCategoriesResponse, GridRequestFilters, Item, PageResponseEnvelope} from '../api/dtos/dtos';
import furoApi from '../api/FuroApi';
import locales from '../locales';
import * as tableUtils from '../utils/table-utils';
import {filterableColumn, GridState, searchableSortableColumn} from '../utils/table-utils';
import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay
} from '../app/ApplicationContext';
import {
  getItemStateFilters,
  mapItemStateToRentalState,
  renderStateElementCondensed,
  RentalState
} from '../common/state';
import {renderItemName} from "./item-name-renderer";
import {renderItemProperties} from "./item-property-renderer";

interface Props {
  allowedStates: RentalState[];
  selectedStates: RentalState[];
  excludedFromSearch: Item[];
  onSelect: (item: Item) => void;
}

interface State extends GridState<Item> {
  categoryFilters: ColumnFilterItem[];
  error?: ErrorResponse;
}

class ItemSelector extends React.Component<Props, State> {

  public readonly loadingHandler?: LoadingOverlayHandler;

  constructor(props: Props) {
    super(props);

    this.state = {
      ...tableUtils.initialGridState(10),
      sortColumnKey: 'name',
      sortOrder: 'ascend',
      filters: {
        ["state"]: [...props.selectedStates]
      },
      categoryFilters: [],
    };
  }

  public componentDidMount() {
    this.loadCategories();
    this.reload();
  }

  public render() {
    const {categoryFilters} = this.state;
    const columns: Array<ColumnProps<Item>> = [
      {
        ...filterableColumn('', 'state', this.state, r => renderStateElementCondensed(r.state), getItemStateFilters(), true),
        width: 38,
      },
      searchableSortableColumn(locales.appellation, 'name', this.state, this.renderNameColumn),
      searchableSortableColumn(locales.inventoryNumber, 'inventoryNumber', this.state, r => r.inventoryNumber),
      filterableColumn(locales.category, 'category', this.state, r => r.category, categoryFilters),
      searchableSortableColumn(locales.resource, 'resource', this.state, r => r.resource),
      searchableSortableColumn(locales.properties, 'details', this.state, renderItemProperties),
      searchableSortableColumn(locales.comment, 'comment', this.state, r => r.comment),
    ];

    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>

        <Table
          dataSource={this.state.data}
          columns={columns}
          rowKey={tableUtils.idRowKeyFn}
          bordered={true}
          size="small"
          pagination={tableUtils.defaultPaginationConfig(this.state)}
          onChange={tableUtils.handleOnGridChange(this.state, this.onGridChange)}
          scroll={{x: 1024}}
        />
        <ErrorAlert style={{marginTop: '16px'}} error={this.state.error}/>
      </>
    );
  }

  private renderNameColumn = (item: Item): React.ReactNode => {
    const {allowedStates} = this.props;
    const onClick = () => this.props.onSelect(item);
    const txt = renderItemName(item);
    const state = mapItemStateToRentalState(item.state);
    return (state && allowedStates.includes(state)) ? (<a onClick={onClick}>{txt}</a>) : (txt);
  };

  private onGridChange = (newState: GridState<Item>) => {
    this.setState({...newState}, () => this.reload());
  };

  private addExcludedItemsToFilters = (filters?: GridRequestFilters): GridRequestFilters | undefined => {
    const {excludedFromSearch} = this.props;
    if (!excludedFromSearch) {
      return filters;
    }
    return {
      ...filters,
      excludedIds: excludedFromSearch.map(item => String(item.id)),
    }
  };

  private loadCategories = () => {
    const promise = furoApi.items.getCategories()
      .then(this.onLoadCategoriesSuccess)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onLoadCategoriesSuccess = (response: GetCategoriesResponse) => {
    this.setState({
      categoryFilters: response.categories.map(c => ({text: c, value: c}))
    });
  };

  private reload = () => {
    const request = {
      ...tableUtils.toGridRequest(this.state),
      filters: this.addExcludedItemsToFilters(this.state.filters),
    };
    const promise = furoApi.items.getAll(request)
      .then(this.onReloadSuccess)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onReloadSuccess = (response: PageResponseEnvelope<Item>) => {
    this.setState({
      ...tableUtils.toGridState(response),
      error: undefined,
    });
  };

  private onApiError = (error: ErrorResponse) => {
    this.setState({error})
  };

}

export default ItemSelector;


// WEBPACK FOOTER //
// ./src/item/ItemSelector.tsx